import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { FaArrowLeft } from 'react-icons/fa';
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "../../Layout/Header";
import { getEndpointUrl, getTimestamp } from "../../Utils/Common";
import AddChoiceDetailDialog from "./AddChoiceDetailDialog";
import { SplitButton } from 'primereact/splitbutton';
import UpdateChoiceDetailDialog from "./UpdateChoiceDetailDialog";
import DeleteChoiceDetailDialog from "./DeleteChoiceDetailDialog";

function DetilChoiceGroup() {
    const { section_id, element_id, choice_group_id } = useParams();
    const [isLoading, setLoading] = useState(true);
    const [accesToken] = useState(JSON.parse(localStorage.getItem('credentials')) || []);
    const [groupMeta, setGroupMeta] = useState(null);
    const [details, setDetails] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const inputRef = useRef();
    const [showCreate, setShowCreate] = useState(false);
    const [showUpdate, setShowUpdate] = useState(false);
    const [updateData, setUpdateData] = useState(null);
    const [deleteData, setDeleteData] = useState(null);
    const [showDelete, setShowDelete] = useState(false);

    const navigate = useNavigate();

    const [userLogin, setUserLogin] = useState(
        JSON.parse(localStorage.getItem('knowLogin')) || []
    );


    const fetchChoiceGroupDetail = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(
                `${getEndpointUrl()}/v2/form_choice/${choice_group_id}?v=${getTimestamp()}`,
                {
                    headers: new Headers({
                        Authorization: `Bearer ${accesToken}`,
                    }),
                }
            );
            const json = await res.json();
            if (json.status === 'ok' && json.results) {
                setGroupMeta(json.results);
                setDetails(json.results.form_choices_details || []);
            } else {
                setGroupMeta(null);
                setDetails([]);
            }
        } catch (err) {
            setGroupMeta(null);
            setDetails([]);
        } finally {
            setLoading(false);
        }
    }, [choice_group_id, accesToken]);

    useEffect(() => {
        fetchChoiceGroupDetail();
    }, [fetchChoiceGroupDetail]);

    const onGlobalFilterChange = (e) => {
        setSearchTerm(e.target.value || '');
    };

    const filteredDetails = (details || []).filter((d) => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return true;
        const text = (d?.detail || '').toLowerCase();
        return text.includes(term);
    });

    const header = (
        <Row>
            <Row>
                <div className="my-2 d-flex align-items-center gap-2">
                    <Link to={`/form-options/${section_id}/${element_id}`} className="btn btn-outline-secondary my-1">
                        <FaArrowLeft /> Kembali
                    </Link>
                </div>
            </Row>
            <Row>
                <Col md={8} sm={12} className="my-2">
                    <h5 className="fw-bold pt-2">
                        Choice Group — {groupMeta?.name || `Group #${choice_group_id}`}
                        <span className="ms-3 badge bg-light text-dark">
                            Required: {groupMeta?.required ? 'Ya' : 'Tidak'}
                        </span>
                    </h5>
                </Col>
                <Col md={4} sm={12} className="my-2 d-flex align-items-center justify-content-end gap-2">
                    <div className="p-input-icon-left" style={{ minWidth: '280px' }}>
                        <i className="pi pi-search" />
                        <InputText
                            ref={inputRef}
                            className="p-inputtext-sm"
                            onChange={onGlobalFilterChange}
                            placeholder="Pencarian detail pilihan..."
                            style={{ minWidth: '100%' }}
                        />
                    </div>
                    <div>
                        <Button
                            className="btn btn-secondary"
                            onClick={() => {
                                setShowCreate(true);
                            }}>
                            Tambah
                        </Button>
                    </div>
                </Col>
            </Row>
        </Row>
    );

    const kelolaBodyTemplate = (rowData) => {
        return <SplitButton size="small" label="Ubah" icon="pi pi-pencil"
            onClick={(e) => {
                setUpdateData(rowData);
                setShowUpdate(true);
            }}
            model={[
                {
                    label: 'Hapus',
                    icon: 'pi pi-trash',
                    disabled: userLogin.role != 'admin',
                    command: () => {
                        setDeleteData(rowData);
                        setShowDelete(true);
                    }
                }
            ]} severity="secondary" text />
    };

    return (
        <>
            <Helmet>
                <title>Detail Choice Group — {groupMeta?.name || choice_group_id}</title>
            </Helmet>
            <Header />
            <Container className="mt-3">
                <DataTable
                    value={filteredDetails}
                    size="small"
                    responsiveLayout="scroll"
                    dataKey="id"
                    header={header}
                    emptyMessage="Tidak ada data yang ditemukan."
                    paginator
                    rows={15}
                    loading={isLoading}
                >
                    <Column field="id" header="ID" style={{ width: '100px' }} />
                    <Column field="detail" header="DETAIL PILIHAN" />
                    <Column body={kelolaBodyTemplate} header="AKSI" style={{ width: '120px' }} />
                </DataTable>
            </Container>

            <AddChoiceDetailDialog
                visible={showCreate}
                onHide={() => setShowCreate(false)}
                choiceGroupId={parseInt(choice_group_id, 10)}
                onSuccess={(updatedGroup) => {
                    setGroupMeta(updatedGroup);
                    setDetails(updatedGroup.form_choices_details || []);
                }}
            />
            <UpdateChoiceDetailDialog
                visible={showUpdate}
                onHide={() => {
                    setShowUpdate(false);
                    setUpdateData(null);
                }}
                data={updateData}
                choiceGroupId={parseInt(choice_group_id, 10)}
                onSuccess={(updated) => {
                    if (updated?.form_choices_details) {
                        setGroupMeta(updated);
                        setDetails(updated.form_choices_details || []);
                    } else if (updated?.id) {
                        setDetails(prev => prev.map(d => d.id === updated.id ? { ...d, ...updated } : d));
                    }
                }}
            />
            <DeleteChoiceDetailDialog
                visible={showDelete}
                onHide={() => {
                    setShowDelete(false);
                    setDeleteData(null);
                }}
                data={deleteData}
                onSuccess={(resp) => {
                    if (resp?.results?.form_choices_details) {
                        setGroupMeta(resp.results);
                        setDetails(resp.results.form_choices_details || []);
                    } else if (resp?.deleted_id) {
                        setDetails(prev => prev.filter(d => d.id !== resp.deleted_id));
                    } else if (deleteData?.id) {
                        setDetails(prev => prev.filter(d => d.id !== deleteData.id));
                    }
                }}
            />
        </>
    );
}

export default DetilChoiceGroup;